import React from "react";
import { Users, UserCheck, UserX, BadgeCheck, AlertTriangle, Info, Layers, ShieldCheck, GitCompareArrows, CheckCircle2 } from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, tone = "" }) => (
  <div className={`kpi glass ${tone}`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <span className="kpi-ico">
      <Icon size={16} />
    </span>
    <div>
      <div className="kpi-value mono">{value?.toLocaleString?.() ?? value}</div>
      <div className="kpi-label">{label}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  </div>
);

export default function ScanOverview({ scan, onCompare }) {
  const s = scan.summary;
  const u = s.users;
  const bases = s.skus.filter((k) => k.kind === "base" && k.consumed > 0);
  const addons = s.skus.filter((k) => (k.kind === "addon" || k.kind === "suite") && k.consumed > 0);
  const seats = bases.reduce((a, k) => a + k.consumed, 0);
  return (
    <div className="ws-stack" data-testid="scan-overview">
      <div className="kpi-grid">
        <Kpi icon={Users} label="Users" value={u.total} sub={`${u.enabled} enabled · ${u.disabled} disabled · ${u.guests} guests`} />
        <Kpi icon={UserCheck} label="Licensed users" value={u.licensed} sub={`${u.unlicensed_members} enabled members without a license`} />
        <Kpi icon={Layers} label="Base suite seats" value={seats} sub={bases.map((b) => `${b.consumed} ${b.name.replace("Microsoft 365 ", "M365 ").replace("Office 365 ", "O365 ")}`).slice(0, 4).join(" · ")} />
        <Kpi icon={BadgeCheck} label="Add-on seats" value={addons.reduce((a, k) => a + k.consumed, 0)} sub={`${addons.length} add-on products in use`} />
        <Kpi icon={UserX} label="Licensed but disabled" value={u.licensed_disabled} tone={u.licensed_disabled ? "warn" : ""} sub="seats to reclaim" />
        <Kpi icon={ShieldCheck} label="CA policies" value={s.ca ? s.ca.total : "n/a"} sub={s.ca ? `${s.ca.enabled} enforced · ${s.ca.report_only} report-only` : s.source === "csv" ? "needs a Graph connection" : "Policy.Read.All not granted"} />
      </div>

      {scan.changes && !scan.changes.first && (
        <section className={`ws-card glass changes ${scan.changes.has_changes ? "has" : ""}`} data-testid="scan-changes">
          <div className="ws-card-head">
            <h2>
              <GitCompareArrows size={15} /> Changes since previous scan
              <span className="muted small">{scan.changes.prev_started_at ? new Date(scan.changes.prev_started_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}</span>
            </h2>
            {onCompare && scan.changes.prev_scan_id && (
              <button type="button" className="tb-btn sm ghost" onClick={() => onCompare(scan.changes.prev_scan_id, scan.id)} data-testid="scan-changes-compare">
                Full comparison
              </button>
            )}
          </div>
          {!scan.changes.has_changes && (
            <p className="muted ok" data-testid="scan-no-changes">
              <CheckCircle2 size={13} /> No changes in seats, users, license gaps or Conditional Access policies.
            </p>
          )}
          {scan.changes.has_changes && (
            <ul className="change-lines" data-testid="scan-change-lines">
              {scan.changes.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="ws-card glass">
        <h2>Insights</h2>
        {s.insights.length === 0 && <p className="muted">Nothing to flag - licenses look tidy.</p>}
        <ul className="insight-list" data-testid="insight-list">
          {s.insights.map((i) => (
            <li key={i.id} className={`insight ${i.level}`} data-testid={`insight-${i.id}`}>
              {i.level === "warn" ? <AlertTriangle size={15} /> : <Info size={15} />}
              <div>
                <b>{i.title}</b>
                <p>{i.detail}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="ws-card glass">
        <h2>Scan details</h2>
        <dl className="detail-grid">
          <div>
            <dt>Microsoft organization</dt>
            <dd>{s.org.display_name}</dd>
          </div>
          <div>
            <dt>Default domain</dt>
            <dd>{s.org.default_domain}</dd>
          </div>
          <div>
            <dt>Verified domains</dt>
            <dd>{s.org.domains.join(", ")}</dd>
          </div>
          <div>
            <dt>Country</dt>
            <dd>{s.org.country || "—"}</dd>
          </div>
          <div>
            <dt>Sign-in activity</dt>
            <dd>{u.sign_in_available ? "available" : s.source === "csv" ? "not in the CSV export" : "not available (AuditLog.Read.All / Entra P1 required)"}</dd>
          </div>
          <div>
            <dt>{s.source === "csv" ? "Source" : "Graph permissions"}</dt>
            <dd className="mono small">{(scan.permissions?.roles || []).join(", ") || "—"}</dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
